import {
  IRegistrationGuest,
  MAX_REGISTRATION_GUESTS,
} from "../interfaces/models/IRegistration";
import { createEmptyRegistrationGuest } from "./registrationGuests";
import type { SpecialEventGuestsMap } from "./specialEventGuests";

export type GuestFormSlot = {
  enabled: boolean;
  name: string;
  specialRequest: string;
  registrationDate?: number;
  modificationDate?: number;
  id?: string;
};

export const GUEST_SLOT_COUNT = MAX_REGISTRATION_GUESTS;

export const createEmptyGuestFormSlot = (
  registrationDate?: number,
): GuestFormSlot => {
  const empty = createEmptyRegistrationGuest(registrationDate);
  return {
    enabled: false,
    name: "",
    specialRequest: "",
    registrationDate: empty.registrationDate,
    modificationDate: empty.modificationDate,
    id: empty.id,
  };
};

export const createEmptyGuestFormSlots = (
  registrationDate?: number,
): GuestFormSlot[] =>
  Array.from({ length: GUEST_SLOT_COUNT }, () =>
    createEmptyGuestFormSlot(registrationDate),
  );

export const guestsToFormSlots = (
  guests?: IRegistrationGuest[],
  fallbackRegistrationDate?: number,
): GuestFormSlot[] => {
  const slots = createEmptyGuestFormSlots(fallbackRegistrationDate);
  const list = Array.isArray(guests) ? guests : [];
  for (let i = 0; i < Math.min(list.length, GUEST_SLOT_COUNT); i++) {
    const guest = list[i];
    slots[i] = {
      enabled: true,
      name: guest.name?.trim() ?? "",
      specialRequest: guest.specialRequest?.trim() ?? "",
      registrationDate:
        guest.registrationDate ??
        fallbackRegistrationDate ??
        Math.floor(Date.now() / 1000),
      modificationDate: guest.modificationDate ?? guest.registrationDate,
      id: guest.id,
    };
  }
  return slots;
};

export const formSlotsToGuests = (
  slots: GuestFormSlot[],
  options?: {
    includeRegistrationDate?: boolean;
    fallbackRegistrationDate?: number;
  },
): IRegistrationGuest[] => {
  const rootDate =
    options?.fallbackRegistrationDate ?? Math.floor(Date.now() / 1000);

  const guests: IRegistrationGuest[] = [];
  for (const slot of slots.slice(0, GUEST_SLOT_COUNT)) {
    if (!slot.enabled) {
      continue;
    }
    const name = slot.name.trim();
    if (!name) {
      continue;
    }
    guests.push({
      id: slot.id?.trim() || crypto.randomUUID(),
      name,
      specialRequest: slot.specialRequest.trim() || undefined,
      registrationDate:
        options?.includeRegistrationDate &&
        typeof slot.registrationDate === "number"
          ? slot.registrationDate
          : rootDate,
      modificationDate:
        typeof slot.modificationDate === "number" &&
        Number.isFinite(slot.modificationDate)
          ? slot.modificationDate
          : undefined,
    });
  }
  return guests;
};

export const specialEventGuestsToFormSlots = (
  map: SpecialEventGuestsMap | undefined,
  programId: string,
  fallbackRegistrationDate?: number,
): GuestFormSlot[] =>
  guestsToFormSlots(map?.[programId], fallbackRegistrationDate);

export const mergeSpecialEventFormSlots = (
  base: Record<string, GuestFormSlot[]>,
  programIds: string[],
  fallbackRegistrationDate?: number,
): Record<string, GuestFormSlot[]> => {
  const allIds = [...new Set([...programIds, ...Object.keys(base)])];
  const result: Record<string, GuestFormSlot[]> = {};
  for (const programId of allIds) {
    result[programId] =
      base[programId] ?? createEmptyGuestFormSlots(fallbackRegistrationDate);
  }
  return result;
};

export const specialEventFormSlotsToMap = (
  slotsMap: Record<string, GuestFormSlot[]>,
  selectedProgramIds: string[],
  fallbackRegistrationDate?: number,
): SpecialEventGuestsMap => {
  const selected = new Set(selectedProgramIds.filter(Boolean));
  const result: SpecialEventGuestsMap = {};
  for (const programId of selected) {
    const guests = formSlotsToGuests(slotsMap[programId] ?? [], {
      includeRegistrationDate: true,
      fallbackRegistrationDate,
    });
    if (guests.length > 0) {
      result[programId] = guests;
    }
  }
  return result;
};

export const countEnabledGuestSlots = (slots: GuestFormSlot[]): number =>
  slots.filter((slot) => slot.enabled && slot.name.trim()).length;

export const validateGuestFormSlots = (
  slots: GuestFormSlot[],
  contextLabel = "the Registrant section",
): string | null => {
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (slot.enabled && !slot.name.trim()) {
      const guestLabel = slots.length > 1 ? ` for guest ${i + 1}` : "";
      return `Add Guest is checked in ${contextLabel}${guestLabel}, but Guest Name is missing. Please enter the guest name or uncheck Add Guest to continue.`;
    }
  }
  return null;
};

export const validateAllGuestFormSlots = (
  regularSlots: GuestFormSlot[],
  specialEventSlotsMap: Record<string, GuestFormSlot[]>,
  selectedProgramIds: Set<string>,
  programTitles: Record<string, string> = {},
): string | null => {
  const regularError = validateGuestFormSlots(
    regularSlots,
    "the Registrant section",
  );
  if (regularError) {
    return regularError;
  }

  for (const programId of selectedProgramIds) {
    const slots = specialEventSlotsMap[programId];
    if (!slots) {
      continue;
    }
    const programTitle =
      programTitles[programId]?.trim() || "the selected special event";
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (slot.enabled && !slot.name.trim()) {
        const guestLabel = slots.length > 1 ? ` (guest ${i + 1})` : "";
        return `Add Guest is checked for ${programTitle}${guestLabel}, but Guest Name is missing. Please enter the guest name or uncheck Add Guest to continue.`;
      }
    }
  }
  return null;
};
